import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import SMTChart from '../common/SMTChart';
import { useFormContext } from '../../contexts/FormContext';
import { useAppContext } from '../../contexts/AppContext';
import SMTInfo from '../common/SMTInfo';
import { useParams } from 'react-router-dom';
import { buildSoilingRatiosParams, buildSoilingRatiosBody } from '../../utils/utils';

const CallContent = ({ field }: { field: any }) => {
  const { formData, getFormDataItem } = useFormContext();
  const { role } = useAppContext();
  const params = useParams();

  let content = <></>;
  let isHidden = field.hasOwnProperty('isHidden') ? getFormDataItem(formData, field?.isHidden) : false;

  if (field.hasOwnProperty('revertHidden') && field?.revertHidden) {
    isHidden = !isHidden;
  }

  if (isHidden) {
    return null;
  }

  switch (field?.content?.component) {
    case 'chart':
      if (
        formData?.location?.latitude &&
        formData?.location?.longitude &&
        !isNaN(Number(formData.location.latitude)) &&
        !isNaN(Number(formData.location.longitude))
      ) {
        // gather parameters
        let requestParams;

        // Use shared utility for soiling-ratios endpoint
        if (field.url === 'soiling-ratios') {
          requestParams = buildSoilingRatiosParams(formData, getFormDataItem, params.id, role);
        } else {
          requestParams = field?.params?.reduce((acc: any, key: string) => {
            const value = getFormDataItem(formData, key);
            const keyName = key.split('.').pop() || key;
            typeof value === 'object' ? Object.assign(acc, value) : Object.assign(acc, { [keyName]: value });
            return acc;
          }, {});
        }

        const body = field?.body?.reduce((acc: any, key: string) => {
          const value = getFormDataItem(formData, key);
          typeof value === 'object' ? Object.assign(acc, value) : Object.assign(acc, { [key]: value });
          // Object.assign(acc, {[key]: value})
          return acc;
        }, {});

        // Special handling for yield-evaluation endpoint
        let chartBody = body;
        if (field.url === 'tools/yield-evaluation') {
          // For yield-evaluation, separate use_plant_geometry from plant object
          const { use_plant_geometry, ...plantData } = body;
          // Check the value of the select
          const yieldSource = getFormDataItem(formData, 'yield_evaluation.source');

          let yield_pattern = null;
          if (yieldSource === 'api') {
            // Option 1: Via API - yield_pattern is null
            yield_pattern = null;
          } else if (yieldSource === 'csv') {
            // Option 2: Via CSV upload - use uploaded pattern only if it's validated
            const pattern = getFormDataItem(formData, 'yield_evaluation.pattern');
            // Don't send the pattern if it's a file path (string)
            yield_pattern = pattern && typeof pattern !== 'string' ? pattern : null;
          } else if (yieldSource === 'monthly') {
            yield_pattern = {
              dates: Array.from({ length: 12 }, (_, i) => `1900-${String(i + 1).padStart(2, '0')}-01`),
              values: formData?.yield_evaluation?.monthly_values || Array(12).fill(0),
            };
          }

          chartBody = { plant: plantData, use_plant_geometry, yield_pattern };
        } else if (field.url === 'soiling-ratios') {
          // Use shared utility for soiling-ratios endpoint to ensure cache consistency
          chartBody = buildSoilingRatiosBody(formData, getFormDataItem, field?.pattern);
        } else {
          // For other endpoints, use the pattern transformation
          chartBody = body?.from_api
            ? { user_patterns: { [field?.pattern + '_from_api']: body?.from_api, [field?.pattern]: null } }
            : { user_patterns: { [field?.pattern + '_from_api']: body?.from_api, [field?.pattern]: body?.[field?.pattern] } };
        }

        // Special handling for yield-evaluation: don't block chart when pattern is uploaded or manual values are provided
        const shouldShowChart = field.url === 'tools/yield-evaluation' ? true : typeof body?.[field?.pattern] !== 'string';

        content = shouldShowChart ? (
          <SMTChart
            upload={field?.upload}
            properties={field?.content?.properties}
            method={field?.method ?? 'get'}
            params={requestParams}
            body={chartBody}
            url={field.url}
            title={field?.title}
            getDataFromApi={field?.get_data_from_api ? getFormDataItem(formData, field.get_data_from_api) : false}
            data={getFormDataItem(formData, field.name)}
            data_type={field?.data_type}
          />
        ) : (
          <></>
        );
        break;
      } else {
        return (
          <Box sx={{ mt: 0 }}>
            <Typography color="text.primary">Missing location data, specify plant location first.</Typography>
          </Box>
        );
      }
    case 'info':
      let infoParams;

      // Use shared utility for soiling-ratios endpoint
      if (field.url === 'soiling-ratios') {
        infoParams = buildSoilingRatiosParams(formData, getFormDataItem, params.id, role);
      } else {
        infoParams = field?.params?.reduce((acc: any, key: string) => {
          const value = getFormDataItem(formData, key);
          typeof value === 'object' ? Object.assign(acc, value) : Object.assign(acc, { [key]: value });
          return acc;
        }, {});
      }

      const body = field?.body?.reduce((acc: any, key: string) => {
        const value = getFormDataItem(formData, key);
        //typeof value === 'object' ? Object.assign(acc, value) : Object.assign(acc, {[key]: value})
        Object.assign(acc, { [key]: value });
        return acc;
      }, {});
      return (
        <SMTInfo
          properties={field?.content?.properties}
          method={field?.method ?? 'get'}
          params={infoParams}
          body={body}
          url={field.url}
          title={field?.title}
        />
      );
    default:
      content = <span>Unknown content_type</span>;
  }
  return (
    <Grid size={field.size || 12} key={field.name}>
      {content}
    </Grid>
  );
};

export default CallContent;
